

































import {Component, Vue} from 'vue-property-decorator';
import {Inject} from 'vue-typedi';
import Tab from "@/components/Tab.vue";
import SearchBar from "@/components/SearchBar.vue";
import TotalOrderNumber from "@/components/TotalOrderNumber.vue";
import PreparationOrderPanel from "@/components/preparation/PreparationOrderPanel.vue";
import {PreparationService} from "@/services/PreparationService";
import {PreparationOrder} from "@/model/preparation/PreparationOrder";
import {PreparationTabType} from "@/model/preparation/PreparationTabType";
import {EventBus} from "@/helpers/EventBus";
import {OrdersListSummary} from "@/model/OrdersListSummary";
import { PreparationEventsService } from '@/services/PreparationEventsService';
import { PreparationOrdersEvent } from '@/model/events/PreparationOrdersEvent';

@Component({
  components: {Tab, SearchBar, TotalOrderNumber, PreparationOrderPanel}
})
export default class PreparationBody extends Vue {

  @Inject()
  preparationService!: PreparationService;
  @Inject()
  preparationEventsService: PreparationEventsService;

  private static readonly REFRESH_INTERVAL_MS: number = 30000;

  private toPrepareOrdersRaw: PreparationOrder[] = [];
  private finishedOrdersRaw: PreparationOrder[] = [];
  private activeTab: PreparationTabType = PreparationTabType.TO_PREPARE;
  private openedCards: string[] = [];
  private searchTerms: string = null;
  private refreshInterval: any = null;

  created(): void {
    if (this.isPreparationOrdersEventsEnabled) {
      this.preparationEventsService.watchOrders();
      EventBus.$on(EventBus.ORDERS_LIST_SERVER_UPDATE, this.refreshEventData);
    } else {  
      this.refreshOrders();
      this.refreshInterval = setInterval(() => this.refreshOrders(), PreparationBody.REFRESH_INTERVAL_MS);
    }

    EventBus.$on(EventBus.UPDATE_SEARCH, this.onUpdateSearch);
  }

  beforeDestroy(): void {
    if (this.isPreparationOrdersEventsEnabled) {
      this.preparationEventsService.unwatchOrders();
      EventBus.$off(EventBus.ORDERS_LIST_SERVER_UPDATE, this.refreshEventData);
    } else {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }

    EventBus.$off(EventBus.UPDATE_SEARCH, this.onUpdateSearch);
  }

  private onUpdateSearch(searchTerms: string): void {
    this.searchTerms = searchTerms;
  }

  refreshOrders(): void {
    if (this.isPreparationOrdersEventsEnabled) {
      return;
    }

    Promise.all([
      this.preparationService.getOrdersToPrepare(),
      this.preparationService.getFinishedOrders()
    ]).then(([toPrepare, finished]) => {
      this.toPrepareOrdersRaw = toPrepare;
      this.finishedOrdersRaw = finished;
      this.emitSummary();
    });
  }

  refreshEventData(event: PreparationOrdersEvent): void {
    this.toPrepareOrdersRaw = [ ...event?.orders?.ordersToProcess ];
    this.finishedOrdersRaw = [ ...event?.orders?.ordersProcessed, ...event?.orders?.ordersArchived ];

    EventBus.$emit(EventBus.REFRESH_ORDERS_LIST, event);
  }

  private emitSummary(): void {
    const allOrders = [...this.toPrepareOrdersRaw, ...this.finishedOrdersRaw];
    const summary = new OrdersListSummary();
    summary.totalOrders = allOrders.length;
    summary.urgentOrders = allOrders.filter(order => order.urgent).length;
    EventBus.$emit(EventBus.REFRESH_ORDERS_LIST, {summary});
  }

  setActiveTab(tab: PreparationTabType): void {
    this.activeTab = tab;
  }

  toggleCardFolding(orderNumber: string): void {
    if (this.isCardOpened(orderNumber)) {
      this.openedCards = this.openedCards.filter(id => id !== orderNumber);
    } else {
      this.openedCards.push(orderNumber);
    }
  }

  isCardOpened(orderNumber: string): boolean {
    return this.openedCards.includes(orderNumber);
  }

  get toPrepareOrders(): PreparationOrder[] {
    return this.toPrepareOrdersRaw
        .filter(this.isMatchingWithSearchTerms)
        .sort(this.byPickupDate);
  }

  get finishedOrders(): PreparationOrder[] {
    return this.finishedOrdersRaw
        .filter(this.isMatchingWithSearchTerms)
        .sort(this.byPickupDate);
  }

  private isMatchingWithSearchTerms(order: PreparationOrder): boolean {
    return !this.searchTerms || this.searchTerms.trim() === '' || order.orderNumber.includes(this.searchTerms);
  }

  get displayedOrders(): PreparationOrder[] {
    return this.isToPrepareTabActive ? this.toPrepareOrders : this.finishedOrders;
  }

  get isToPrepareTabActive(): boolean {
    return this.activeTab === PreparationTabType.TO_PREPARE;
  }

  get isDeliveredTabActive(): boolean {
    return this.activeTab === PreparationTabType.DELIVERED;
  }

  get tabToPrepare(): PreparationTabType {
    return PreparationTabType.TO_PREPARE;
  }

  get tabDelivered(): PreparationTabType {
    return PreparationTabType.DELIVERED;
  }

  private byPickupDate(a: PreparationOrder, b: PreparationOrder): number {
    return new Date(a.pickupDate).getTime() - new Date(b.pickupDate).getTime();
  }

  get isPreparationOrdersEventsEnabled(): boolean {
    const config = (window as any)?.config;

    return config?.events?.enabled && config?.events?.preparation?.enabled;
  }
}
